import React from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Button, Typography, Grid, IconButton, TextField,
    Tabs, Tab, Stack, Box, Chip,
    alpha, useMediaQuery, Paper, Dialog, DialogTitle,
    DialogContent, DialogActions, DialogContentText,
} from '@mui/material';
import { Edit, Delete, CheckCircle, VerifiedUser, Download, Save } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import BadgeIcon from '@mui/icons-material/Badge';
import moment from 'moment';
import { getRole, getUserName, httpClient, processUrl } from 'Service/Service';
import toast from 'react-hot-toast';
import CustomDrawer from '../common/CustomerDrawer';

const InfoRow = ({ label, value }: { label: string; value?: string }) => (
    <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ lineHeight: 1.3 }}>
            {label}
        </Typography>
        <Typography variant="body2" fontWeight={value && value !== 'NA' ? 500 : 400} color={value && value !== 'NA' ? 'text.primary' : 'text.disabled'}>
            {value || 'NA'}
        </Typography>
    </Box>
);

const CustomerDetails = ({ user, searchType }: any) => {
    const theme = useTheme();
    return (
        <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <Grid container spacing={2}>
                {/* Personal Details */}
                <Grid item xs={12} md={6}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            height: '100%',
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                        }}
                    >
                        <Typography variant="subtitle2" fontWeight={700} color="primary.main" mb={1.5}>
                            Personal Details
                        </Typography>
                        <Stack spacing={1.5}>
                            <InfoRow label="Name" value={user.name?.toUpperCase()} />
                            <InfoRow label="Mobile" value={user.mobile} />
                            <InfoRow label="Contact Number" value={user.contactNumber} />
                            <InfoRow label="Main Aadhaar" value={user.mainAadhaar} />
                            <InfoRow label="Family Aadhaar" value={user.familyAadhaar} />
                        </Stack>
                    </Paper>
                </Grid>

                {/* Agency Details */}
                <Grid item xs={12} md={6}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            height: '100%',
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderLeft: `3px solid ${theme.palette.secondary.main}`,
                        }}
                    >
                        <Typography variant="subtitle2" fontWeight={700} color="secondary.main" mb={1.5}>
                            Agency Details
                        </Typography>
                        <Stack spacing={1.5}>
                            <InfoRow label="Main Agent" value={user.mainAgent?.toUpperCase()} />
                            <InfoRow label="Sub Agent" value={user.subAgent} />
                            <InfoRow label="Registered Agency" value={user.registeredAgencyName} />
                            {searchType === 'new' && (
                                <InfoRow label="New Registered Agency" value={user.newRegisteredAgency} />
                            )}
                        </Stack>
                    </Paper>
                </Grid>

                {/* Connection Details */}
                <Grid item xs={12}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderLeft: `3px solid #ff9800`,
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                            <Typography variant="subtitle2" fontWeight={700} color="#e65100">
                                Connection Details
                            </Typography>
                            {user.consumerNo && (
                                <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                            )}
                            {user.registrationStatus === 'Complete' && (
                                <Chip
                                    label="Complete"
                                    size="small"
                                    color="success"
                                    sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
                                />
                            )}
                        </Stack>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Stack spacing={1.5}>
                                    <InfoRow label="Consumer No" value={user.consumerNo} />
                                    <InfoRow label="Registration Status" value={user.registrationStatus} />
                                    <InfoRow label="Registration No" value={user.regNo} />
                                    {searchType === 'new' && (
                                        <InfoRow label="Active Consumer No" value={user.newConsumerNo} />
                                    )}
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Stack spacing={1.5}>
                                    {searchType === 'new' && (
                                        <>
                                            <InfoRow label="Single Women" value={user.isSingleWomen ? 'YES' : 'NO'} />
                                            <InfoRow label="Free Delivery" value={user.isFreeDelivery ? 'YES' : 'NO'} />
                                        </>
                                    )}
                                    <InfoRow label="Created On" value={moment(user.createdAt).format('LLL')} />
                                    {user.updatedAt && (
                                        <InfoRow label="Updated On" value={moment(user.updatedAt).format('LLL')} />
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Documents */}
                {(user.InstalationLetter || user.satisfactionLetter || user.otherLetter || user.swasthyaSathiLetter) && (
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${alpha('#8bc34a', 0.4)}`,
                                bgcolor: alpha('#8bc34a', 0.04),
                                borderLeft: `3px solid #8bc34a`,
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight={700} color="#558b2f" mb={1.5}>
                                Linked Documents
                            </Typography>
                            <Grid container spacing={2}>
                                {[
                                    { key: user.InstalationLetter, label: 'Installation Letter' },
                                    { key: user.satisfactionLetter, label: 'Satisfaction Letter' },
                                    { key: user.otherLetter, label: 'Other Document' },
                                    { key: user.swasthyaSathiLetter, label: 'Swasthya Sathi Letter' },
                                ]
                                    .filter((d) => d.key)
                                    .map((doc) => (
                                        <Grid item xs={12} sm={6} md={3} key={doc.label}>
                                            <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', border: `1px solid ${alpha('#000', 0.08)}`, borderRadius: 2 }}>
                                                <img
                                                    src={processUrl(doc.key)}
                                                    alt={doc.label}
                                                    style={{ maxWidth: '100%', height: 'auto', maxHeight: '160px', borderRadius: 4 }}
                                                />
                                                <Typography variant="caption" display="block" sx={{ mt: 0.75, fontWeight: 600 }}>
                                                    {doc.label}
                                                </Typography>
                                                <Button
                                                    href={processUrl(doc.key)}
                                                    target="_blank"
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<Download />}
                                                    sx={{ mt: 1, borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
                                                    fullWidth
                                                >
                                                    Download
                                                </Button>
                                            </Paper>
                                        </Grid>
                                    ))}
                            </Grid>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

const ConsumerSearch = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [users, setUsers] = React.useState<any[]>([]);
    const [searchType, setSearchType] = React.useState('new');
    const [open, setOpen] = React.useState(false);
    const [openAlert, setOpenAlert] = React.useState(false);
    const [agentList, setAgentList] = React.useState([]);
    const [tabValue, setTabValue] = React.useState(0);
    const [selectedUser, setSelectedUser] = React.useState<any>(null);
    const [drawerOpen, setDrawerOpen] = React.useState(false);

    const [state, setState] = React.useState({
        regNo: '',
        mobile: '',
        aadhaar: '',
        consumerNo: '',
        fileNumber: '',
        familyAadhaar: '',
    });

    const [customer, setCustomer] = React.useState<any>({});

    const handleCardClick = (user: any) => {
        setSelectedUser(user);
        setDrawerOpen(true);
    };

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        setSearchType(newValue === 0 ? 'new' : 'old');
        setUsers([]);
        setState({ regNo: '', mobile: '', aadhaar: '', consumerNo: '', fileNumber: '', familyAadhaar: '' });
    };

    const handleChange = (event: any) => {
        setState({ ...state, [event.target.name]: event.target.value });
    };

    const handleFind = async () => {
        try {
            const searchCriteria: any = {
                mobile: state.mobile,
                mainAadhaar: state.aadhaar,
                consumerNo: state.consumerNo,
                familyAadhaar: state.familyAadhaar,
                regNo: state.fileNumber,
            };
            const findKey = Object.keys(searchCriteria).find((key) => searchCriteria[key]);
            if (!findKey) return;

            const endpoint = searchType === 'old' ? 'old/customer/find' : 'customer/find';
            const payload = {
                findkey: findKey === 'mainAadhaar' ? 'mainAadhaar' : findKey,
                [findKey]: searchCriteria[findKey],
            };

            const result: any = await httpClient(endpoint, 'POST', payload);

            if (!result.data?.length && searchType === 'old') return toast.error('No result found');
            if (!result.data && searchType === 'new') return toast.error('No result found');

            setUsers(searchType === 'new' ? [result.data] : (result.data as any[]));
            setCustomer(searchType === 'new' ? result.data : result.data[0]);
            setDrawerOpen(true);
            getAllAgents();
        } catch {
            toast.error('Something went wrong');
        }
    };

    const handleUpdate = async () => {
        try {
            const endpoint = searchType === 'old' ? 'old/customer/update' : 'customer/update';
            const payload =
                searchType === 'old'
                    ? { data: customer }
                    : { ...customer, updatedBy: getUserName() };
            const result = await httpClient(endpoint, 'POST', payload);
            //@ts-ignore
            if (result.data.data) {
                toast.success('Customer updated successfully');
                setOpen(false);
            }
        } catch {
            toast.error('Update failed');
        }
    };

    const handleDelete = async (customer: any) => {
        try {
            const endpoint = searchType === 'old' ? 'old/customer/delete' : 'customer/delete';
            const result = await httpClient(endpoint, 'POST', { customerId: customer._id });
            if (result.data) {
                toast.success('Customer deleted successfully');
                setOpenAlert(false);
                setUsers([]);
            }
        } catch {
            toast.error('Delete failed');
        }
    };

    const getAllAgents = async () => {
        try {
            const result = await httpClient('agent/getall/active', 'GET');
            //@ts-ignore
            if (result.data.data.agents.length) {
                //@ts-ignore
                setAgentList(result.data.data.agents.map(({ name }: any) => ({ label: name, value: name })));
            }
        } catch {
            toast.error('Failed to fetch agents');
        }
    };

    const renderCardActions = (user: any) => (
        <Stack direction="row" spacing={0.5}>
            {user.installtatus === 'Complete' && (
                <IconButton size="small">
                    {searchType === 'new' ? (
                        <VerifiedUser sx={{ color: 'secondary.main', fontSize: 18 }} />
                    ) : (
                        <CheckCircle sx={{ color: 'primary.main', fontSize: 18 }} />
                    )}
                </IconButton>
            )}
            {(getRole() === 'superadmin' || (getRole() === 'user' && user.mainAgent === getUserName())) && (
                <IconButton
                    size="small"
                    onClick={() => { setCustomer(user); setOpen(true); }}
                    sx={{ color: 'primary.main', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
                >
                    <Edit fontSize="small" />
                </IconButton>
            )}
            {getRole() === 'superadmin' && (
                <IconButton
                    size="small"
                    onClick={() => { setCustomer(user); setOpenAlert(true); }}
                    sx={{ color: 'error.main', '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}
                >
                    <Delete fontSize="small" />
                </IconButton>
            )}
        </Stack>
    );

    const searchFields = [
        { label: 'Main Aadhaar', name: 'aadhaar', icon: <FingerprintIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, type: 'tel' },
        { label: 'Family Aadhaar', name: 'familyAadhaar', icon: <FingerprintIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, type: 'tel' },
        { label: 'Mobile No', name: 'mobile', icon: <PhoneAndroidIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, type: 'tel', maxLength: 10 },
        { label: 'Consumer No', name: 'consumerNo', icon: <BadgeIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, type: 'text' },
    ];

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            {/* Page Header */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.primary.light} 100%)`,
                    px: { xs: 2, sm: 4 },
                    py: { xs: 2.5, sm: 3.5 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderRadius: 1,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                }}
            >
                <Box
                    sx={{
                        bgcolor: alpha('#fff', 0.15),
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <PersonSearchIcon sx={{ color: '#fff', fontSize: { xs: 28, sm: 36 } }} />
                </Box>
                <Box>
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        fontWeight={700}
                        sx={{ color: '#fff', lineHeight: 1.2 }}
                    >
                        Find Consumer
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.8), mt: 0.3 }}>
                        Search by Aadhaar, mobile or consumer number
                    </Typography>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ px: { xs: 1, sm: 3 }, pt: 2 }}>
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        boxShadow: `0 2px 12px ${alpha(theme.palette.grey[500], 0.12)}`,
                        overflow: 'hidden',
                    }}
                >
                    {/* Tabs */}
                    <Box
                        sx={{
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            px: { xs: 1, sm: 2 },
                            pt: 1,
                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                        }}
                    >
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            variant={isMobile ? 'fullWidth' : 'standard'}
                            TabIndicatorProps={{
                                sx: {
                                    height: 3,
                                    borderRadius: '3px 3px 0 0',
                                    bgcolor: 'primary.main',
                                },
                            }}
                            sx={{
                                minHeight: 48,
                                '& .MuiTab-root': {
                                    minHeight: 48,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    color: 'text.secondary',
                                    '&.Mui-selected': { color: 'primary.main' },
                                },
                            }}
                        >
                            <Tab label="New Consumer" />
                            <Tab label="Old Consumer" />
                        </Tabs>
                    </Box>

                    <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: 3, pt: 3 }}>
                        {/* Search Form */}
                        <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{ mb: 2, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}
                        >
                            Search Criteria — enter one field
                        </Typography>

                        <Paper
                            elevation={0}
                            sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                p: { xs: 1.5, sm: 2.5 },
                                mb: 3,
                            }}
                        >
                            <Grid container spacing={2} alignItems="flex-end">
                                {searchFields.map((field) => (
                                    <Grid item xs={12} sm={6} md={3} key={field.name}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                            {field.label}
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            name={field.name}
                                            type={field.type}
                                            value={(state as any)[field.name]}
                                            onChange={handleChange}
                                            placeholder={`Enter ${field.label}`}
                                            inputProps={{ maxLength: field.maxLength }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ mr: 0.5, display: 'flex' }}>{field.icon}</Box>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                ))}
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<SearchIcon />}
                                        onClick={handleFind}
                                        fullWidth
                                        sx={{
                                            borderRadius: 2,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            height: 40,
                                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
                                        }}
                                    >
                                        Find Consumer
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Results */}
                        {users.length > 0 && (
                            <Box>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                        sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}
                                    >
                                        {users.length} Result{users.length > 1 ? 's' : ''} Found
                                    </Typography>
                                </Stack>

                                <Stack spacing={2}>
                                    {users.map((user, index) => (
                                        <Paper
                                            key={index}
                                            elevation={0}
                                            onClick={() => handleCardClick(user)}
                                            sx={{
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    borderColor: alpha(theme.palette.primary.main, 0.35),
                                                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
                                                },
                                            }}
                                        >
                                            {/* Card header */}
                                            <Box
                                                sx={{
                                                    px: 2,
                                                    py: 1,
                                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                                                        {user.name?.toUpperCase() || 'N/A'}
                                                    </Typography>
                                                    {user.consumerNo && (
                                                        <Chip
                                                            label={`#${user.consumerNo}`}
                                                            size="small"
                                                            sx={{
                                                                height: 18,
                                                                fontSize: '0.65rem',
                                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                                color: 'primary.dark',
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    )}
                                                    {user.registrationStatus === 'Complete' && (
                                                        <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                                                    )}
                                                </Stack>
                                                {renderCardActions(user)}
                                            </Box>

                                            {/* Card body */}
                                            <CustomerDetails user={user} searchType={searchType} />
                                        </Paper>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Drawer */}
            {selectedUser && (
                <CustomDrawer
                    title={`Customer — ${selectedUser.name?.toUpperCase() || 'NA'}`}
                    handleOpen={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    variant="small"
                >
                    <CustomerDetails user={selectedUser} searchType={searchType} />
                </CustomDrawer>
            )}

            {/* Edit Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Edit Customer</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        {[
                            { label: 'Name', field: 'name' },
                            { label: 'Mobile', field: 'mobile' },
                            { label: 'Contact Number', field: 'contactNumber' },
                            { label: 'Main Aadhaar', field: 'mainAadhaar' },
                            { label: 'Family Aadhaar', field: 'familyAadhaar' },
                            { label: 'Consumer No', field: 'consumerNo' },
                            { label: 'Main Agent', field: 'mainAgent' },
                            { label: 'Sub Agent', field: 'subAgent' },
                            { label: 'Registered Agency', field: 'registeredAgencyName' },
                        ].map(({ label, field }) => (
                            <Grid item xs={12} sm={6} key={field}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                    {label}
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={customer?.[field] || ''}
                                    onChange={(e) => setCustomer({ ...customer, [field]: e.target.value })}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpen(false)} variant="outlined" color="inherit" sx={{ textTransform: 'none', borderRadius: 2 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpdate}
                        variant="contained"
                        startIcon={<Save />}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openAlert} onClose={() => setOpenAlert(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>Delete Customer</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <strong>{customer?.name}</strong>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpenAlert(false)} variant="outlined" color="inherit" sx={{ textTransform: 'none', borderRadius: 2 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleDelete(customer)}
                        variant="contained"
                        color="error"
                        startIcon={<Delete />}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ConsumerSearch;
